import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import App from './App';
import LoginPage from './pages/Login';
import MyTicketsPage from './pages/MyTickets';
import QueuesPage from './pages/Queues';
import HomePage from './pages/Home';
import { FEATURES } from './lib/features';
import { AuthGuard } from './components/AuthGuard';
import { ROUTES, LEGACY_REDIRECTS, LEGACY_RECORD_REDIRECTS } from './lib/routes';
import { LazyPage } from './components/LazyPage';
import './index.css';

/**
 * Forwards a pre-`/psa` record URL (e.g. `/tickets/123`) to its new home,
 * carrying the id. Links copied out of an AI-assistant chat still land.
 */
function LegacyRecordRedirect({ build }: { build: (id: string) => string }) {
  const { id } = useParams();
  return <Navigate to={build(id ?? '')} replace />;
}

/** `React.lazy` for a module's named export (pages that export several components). */
function lazyNamed<M extends Record<string, unknown>, K extends keyof M>(load: () => Promise<M>, name: K) {
  return React.lazy(async () => ({ default: (await load())[name] as React.ComponentType<any> }));
}

// Every page except the first-paint ones (Login, Home, My tickets, Queues) is code-split: the main chunk must stay
// well under the PWA's 2 MiB precache limit, and recharts / react-grid-layout / postal-mime / marked only load with
// the pages that use them. App wraps its <Outlet/> in one Suspense (LazyPage); Settings pages have their own in
// SettingsLayout.
const TicketDetailPage = React.lazy(() => import('./pages/TicketDetail'));
const OpportunitiesPage = React.lazy(() => import('./pages/Opportunities'));
const OpportunityDetailPage = React.lazy(() => import('./pages/OpportunityDetail'));
const AccountPage = React.lazy(() => import('./pages/Account'));
const AccountInfoPage = React.lazy(() => import('./pages/AccountInfo'));
const AccountNoteTemplatesPage = React.lazy(() => import('./pages/AccountNoteTemplates'));
const AccountBookmarksPage = React.lazy(() => import('./pages/AccountBookmarks'));
const AdminPage = React.lazy(() => import('./pages/Admin'));
const CippTenantsPage = React.lazy(() => import('./pages/cipp/Tenants'));
const CippTenantDetailPage = React.lazy(() => import('./pages/cipp/TenantDetail'));
const CippDevicesPage = React.lazy(() => import('./pages/cipp/Devices'));
const CippSecurityDashboardPage = React.lazy(() => import('./pages/cipp/SecurityDashboard'));
const DomainsPage = React.lazy(() => import('./pages/domains/Domains'));
const DomainDetailPage = React.lazy(() => import('./pages/domains/DomainDetail'));
const DocsOrganizationsPage = React.lazy(() => import('./pages/docs/Organizations'));
const DocsOrganizationPage = React.lazy(() => import('./pages/docs/Organization'));
const DocsConfigurationsPage = React.lazy(() => import('./pages/docs/Configurations'));
const DocsExpirationsPage = React.lazy(() => import('./pages/docs/Expirations'));
const NetworkHomePage = React.lazy(() => import('./pages/network/NetworkHome'));
const SophosAlertsPage = lazyNamed(() => import('./pages/network/Sophos'), 'SophosAlertsPage');
const SophosDashboardPage = lazyNamed(() => import('./pages/network/Sophos'), 'SophosDashboardPage');
const SophosFirewallsPage = lazyNamed(() => import('./pages/network/Sophos'), 'SophosFirewallsPage');
const SophosSubscriptionsPage = lazyNamed(() => import('./pages/network/Sophos'), 'SophosSubscriptionsPage');
const SophosTenantsPage = lazyNamed(() => import('./pages/network/Sophos'), 'SophosTenantsPage');
const UnifiConsolesPage = lazyNamed(() => import('./pages/network/Unifi'), 'UnifiConsolesPage');
const UnifiDashboardPage = lazyNamed(() => import('./pages/network/Unifi'), 'UnifiDashboardPage');
const UnifiDevicesPage = lazyNamed(() => import('./pages/network/Unifi'), 'UnifiDevicesPage');
const UnifiSitesPage = lazyNamed(() => import('./pages/network/Unifi'), 'UnifiSitesPage');
const UnifiWanPage = lazyNamed(() => import('./pages/network/Unifi'), 'UnifiWanPage');
const InsightsCoveragePage = React.lazy(() => import('./pages/insights/Coverage'));
const InsightsGapsPage = React.lazy(() => import('./pages/insights/Gaps'));
const FindPage = React.lazy(() => import('./pages/insights/Find'));
const SocOverviewPage = React.lazy(() => import('./pages/soc/SocOverview'));
const SocIncidentsPage = React.lazy(() => import('./pages/soc/SocIncidents'));
const SocIncidentPage = React.lazy(() => import('./pages/soc/SocIncident'));
const SocAgentsPage = React.lazy(() => import('./pages/soc/SocAgents'));
const SocIdentitiesPage = React.lazy(() => import('./pages/soc/SocIdentities'));
const SocOrganizationsPage = React.lazy(() => import('./pages/soc/SocOrganizations'));
const AiChatPage = React.lazy(() => import('./pages/ai/AiChat'));
const DashboardsPage = React.lazy(() => import('./pages/dashboards/Dashboards'));
const DashboardPage = React.lazy(() => import('./pages/dashboards/Dashboard'));
const DashboardDisplayPage = React.lazy(() => import('./pages/dashboards/DashboardDisplay'));
const DeviceOrbitPage = React.lazy(() => import('./pages/insights/DeviceOrbit'));
const PersonOrbitPage = React.lazy(() => import('./pages/insights/PersonOrbit'));
const CompaniesPage = React.lazy(() => import('./pages/companies/Companies'));
const OrbitPage = React.lazy(() => import('./pages/companies/Orbit'));
const BackupSaasCustomersPage = React.lazy(() => import('./pages/backup/SaasCustomers'));
const BackupSaasCustomerPage = React.lazy(() => import('./pages/backup/SaasCustomer'));
const CspDashboardPage = React.lazy(() => import('./pages/csp/CspDashboard'));
const CspCustomersPage = React.lazy(() => import('./pages/csp/CspCustomers'));
const CspLicencesPage = React.lazy(() => import('./pages/csp/CspLicences'));
const CspProductsPage = React.lazy(() => import('./pages/csp/CspProducts'));
const CspRequestsPage = React.lazy(() => import('./pages/csp/CspRequests'));
const CspUploadPage = React.lazy(() => import('./pages/csp/CspUpload'));
const CspMappingsPage = React.lazy(() => import('./pages/csp/CspMappings'));
const CreateTicketPage = React.lazy(() => import('./pages/CreateTicket'));
const TicketSearchPage = React.lazy(() => import('./pages/TicketSearch'));
const CreateOpportunityPage = React.lazy(() => import('./pages/CreateOpportunity'));
const OpportunitySearchPage = React.lazy(() => import('./pages/OpportunitySearch'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const RmmDevicesPage = React.lazy(() => import('./pages/rmm/Devices'));
const RmmDeviceDetailPage = React.lazy(() => import('./pages/rmm/DeviceDetail'));
const RmmAlertsPage = React.lazy(() => import('./pages/rmm/Alerts'));
const RmmSitesPage = React.lazy(() => import('./pages/rmm/Sites'));

// Dashboards are desktop-only and pull in recharts — lazy-load so that chunk
// stays out of the main/mobile bundle.
const ServiceDeskDashboardPage = React.lazy(() => import('./pages/ServiceDeskDashboard'));
const AccountPreferencesPage = React.lazy(() => import('./pages/AccountPreferences'));
// Settings pages load on demand (SettingsLayout wraps its Outlet in Suspense) — keeps the main chunk under the
// PWA's 2 MiB precache limit.
const AdminUsersPage = React.lazy(() => import('./pages/AdminUsers'));
const AdminWebhookPage = React.lazy(() => import('./pages/AdminWebhook'));
const AdminAiAssistantPage = React.lazy(() => import('./pages/AdminAiAssistant'));
const AdminProjectStatusPage = React.lazy(() => import('./pages/AdminProjectStatus'));
const AdminSystemPage = React.lazy(() => import('./pages/AdminSystem'));
const AdminIntegrationsPage = React.lazy(() => import('./pages/AdminIntegrations'));
const AdminMappingsPage = React.lazy(() => import('./pages/AdminMappings'));
const AdminCippPage = React.lazy(() => import('./pages/AdminCipp'));
const AdminNetworkMappingPage = React.lazy(() => import('./pages/AdminNetworkMapping'));
const AdminModuleSettingsPage = React.lazy(() => import('./pages/AdminModuleSettings'));
const SalesDashboardPage = React.lazy(() => import('./pages/SalesDashboard'));
// Projects are code-split: the main bundle must stay under the PWA's 2 MiB precache limit.
const ProjectsListPage = React.lazy(() => import('./pages/projects/ProjectsList'));
const ProjectDetailPage = React.lazy(() => import('./pages/projects/ProjectDetail'));
const MyProjectTasksPage = React.lazy(() => import('./pages/projects/MyTasks'));
const DispatchPage = React.lazy(() => import('./pages/Dispatch'));
const RmmOverviewDashboardPage = React.lazy(() => import('./pages/rmm/OverviewDashboard'));
const RmmPatchingDashboardPage = React.lazy(() => import('./pages/rmm/PatchingDashboard'));
const RmmRemoteAccessPage = React.lazy(() => import('./pages/rmm/RemoteAccess'));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 60 s: a tab regaining focus refetches only queries older than this (hooks that poll set their own).
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Earlier service workers cached every /api response in 'api-cache' (removed from vite.config.ts) — drop it so
// customer data doesn't linger in Cache Storage.
if ('caches' in window) void caches.delete('api-cache').catch(() => {});

// A code-split chunk failed to load: almost always a tab still running the build before a deploy, whose chunks
// are gone. Reload once onto the new build (at most once a minute, so a real outage can't loop); otherwise the
// error reaches LazyPage's boundary, which offers a reload.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'cosmos:chunkReloadAt';
  let last = 0;
  try { last = Number(sessionStorage.getItem(KEY)) || 0; } catch { /* storage unavailable */ }
  if (Date.now() - last < 60_000) return;
  try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* storage unavailable */ }
  event.preventDefault();
  window.location.reload();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public route — accessible without a session */}
          <Route path="/login" element={<LoginPage />} />

          {/* Wall display: signed in, but outside the app shell (no sidebar / header). */}
          <Route path="/dashboards/:id/display" element={<AuthGuard><LazyPage><DashboardDisplayPage /></LazyPage></AuthGuard>} />

          {/* All app routes require authentication */}
          <Route path="/" element={<AuthGuard><App /></AuthGuard>}>
            <Route index element={<HomePage />} />

            {/* ── PSA module (Autotask) ─────────────────────────────── */}
            <Route path="psa">
              <Route index element={<Navigate to={ROUTES.psa.tickets.mine} replace />} />

              <Route path="dashboards"              element={<Navigate to={ROUTES.psa.dashboards.serviceDesk} replace />} />
              <Route path="dashboards/service-desk" element={<ServiceDeskDashboardPage />} />
              <Route path="dashboards/sales"        element={<SalesDashboardPage />} />

              {/* Static segments outrank `:id` in React Router's ranking, so
                  `tickets/mine` and `tickets/:id` coexist safely. */}
              <Route path="tickets/mine"   element={<MyTicketsPage />} />
              <Route path="tickets/queues" element={<QueuesPage />} />
              <Route path="tickets/search" element={<TicketSearchPage />} />
              <Route path="tickets/new"    element={<CreateTicketPage />} />
              <Route path="tickets/:id"    element={<TicketDetailPage />} />
              <Route path="tickets"        element={<Navigate to={ROUTES.psa.tickets.mine} replace />} />

              <Route path="opportunities/mine"   element={<OpportunitiesPage assignedTo="me" />} />
              <Route path="opportunities/search" element={<OpportunitySearchPage />} />
              <Route path="opportunities/new"    element={<CreateOpportunityPage />} />
              <Route path="opportunities/:id"    element={<OpportunityDetailPage />} />
              <Route path="opportunities"        element={<OpportunitiesPage assignedTo="any" />} />

              <Route path="search" element={<SearchPage />} />

              <Route path="projects"                   element={<ProjectsListPage />} />
              <Route path="projects/mine"              element={<ProjectsListPage mine />} />
              <Route path="projects/tasks/mine"        element={<MyProjectTasksPage />} />
              <Route path="dispatch"                  element={<DispatchPage />} />
              <Route path="projects/:id"               element={<ProjectDetailPage />} />
              <Route path="projects/:id/tasks/:taskId" element={<ProjectDetailPage />} />
            </Route>

            {/* ── RMM module (Datto RMM) ─────────────────────────────── */}
            <Route path="rmm">
              <Route index element={<Navigate to={ROUTES.rmm.dashboards.overview} replace />} />
              <Route path="dashboards" element={<Navigate to={ROUTES.rmm.dashboards.overview} replace />} />
              <Route path="dashboards/overview" element={<RmmOverviewDashboardPage />} />
              <Route path="dashboards/patching" element={<RmmPatchingDashboardPage />} />
              <Route path="devices"      element={<RmmDevicesPage />} />
              <Route path="devices/:uid" element={<RmmDeviceDetailPage />} />
              <Route path="alerts"       element={<RmmAlertsPage />} />
              <Route path="sites"        element={<RmmSitesPage />} />
              <Route path="sites/:uid"   element={<RmmSitesPage />} />
              <Route path="sites/client/:companyId" element={<RmmSitesPage />} />
              <Route path="remote"       element={<RmmRemoteAccessPage />} />
            </Route>

            {/* ── CIPP module (Microsoft 365) ───────────────────────── */}
            <Route path="cipp">
              <Route index element={<Navigate to={ROUTES.cipp.tenants} replace />} />
              <Route path="tenants"             element={<CippTenantsPage />} />
              <Route path="tenants/:customerId" element={<CippTenantDetailPage />} />
              <Route path="devices"             element={<CippDevicesPage />} />
              <Route path="dashboards/security" element={<CippSecurityDashboardPage />} />
            </Route>

            {/* ── CSP module (Microsoft NCE licences) ───────────────── */}
            <Route path="csp">
              <Route index element={<Navigate to={ROUTES.csp.dashboard} replace />} />
              <Route path="dashboard" element={<CspDashboardPage />} />
              <Route path="customers" element={<CspCustomersPage />} />
              <Route path="licences"  element={<CspLicencesPage />} />
              <Route path="products"  element={<CspProductsPage />} />
              <Route path="requests"  element={<CspRequestsPage />} />
              <Route path="upload"    element={<CspUploadPage />} />
              <Route path="mappings"  element={<CspMappingsPage />} />
              {/* CSP settings moved to Admin → CSP (org-wide). */}
              <Route path="settings"  element={<Navigate to={ROUTES.admin.csp} replace />} />
            </Route>

            {/* ── Network (Sophos firewalls + UniFi) ─────────────────── */}
            <Route path="network">
              <Route index element={<NetworkHomePage />} />
              {/* Sophos and UniFi are separate areas with their own data. */}
              <Route path="sophos"               element={<SophosDashboardPage />} />
              <Route path="sophos/firewalls"     element={<SophosFirewallsPage />} />
              <Route path="sophos/subscriptions" element={<SophosSubscriptionsPage />} />
              <Route path="sophos/alerts"        element={<SophosAlertsPage />} />
              <Route path="sophos/tenants"       element={<SophosTenantsPage />} />
              <Route path="unifi"                element={<UnifiDashboardPage />} />
              <Route path="unifi/sites"          element={<UnifiSitesPage />} />
              <Route path="unifi/consoles"       element={<UnifiConsolesPage />} />
              <Route path="unifi/devices"        element={<UnifiDevicesPage />} />
              <Route path="unifi/wan"            element={<UnifiWanPage />} />
              {/* Earlier combined pages (2026-10-13) → their new homes. */}
              <Route path="firewalls" element={<Navigate to={ROUTES.network.sophos.firewalls} replace />} />
              <Route path="devices"   element={<Navigate to={ROUTES.network.unifi.devices} replace />} />
            </Route>

            {/* ── Domains (dmarcian + CIPP DNS) ──────────────────────── */}
            <Route path="domains"         element={<DomainsPage />} />
            <Route path="domains/:domain" element={<DomainDetailPage />} />

            {/* ── SOC (Huntress) — flat pages ─────────────────────────── */}
            <Route path="soc"                element={<SocOverviewPage />} />
            <Route path="soc/incidents"      element={<SocIncidentsPage />} />
            <Route path="soc/incidents/:id"  element={<SocIncidentPage />} />
            <Route path="soc/agents"         element={<SocAgentsPage />} />
            <Route path="soc/identities"     element={<SocIdentitiesPage />} />
            <Route path="soc/organizations"  element={<SocOrganizationsPage />} />

            {/* ── AI: chat with the Foundry agent (Synfo MCP). Parked: lib/features.ts ── */}
            {FEATURES.aiChat && <Route path="ai" element={<AiChatPage />} />}

            {/* ── Custom dashboards (any module's metrics; saved + shared) ── */}
            <Route path="dashboards"     element={<DashboardsPage />} />
            <Route path="dashboards/:id" element={<DashboardPage />} />

            {/* ── Insights (cross-module) + Orbit ──────────────── */}
            <Route path="insights">
              <Route index element={<Navigate to={ROUTES.insights.coverage} replace />} />
              <Route path="coverage" element={<InsightsCoveragePage />} />
              <Route path="gaps" element={<InsightsGapsPage />} />
              <Route path="find" element={<FindPage />} />
              <Route path="devices/:key" element={<DeviceOrbitPage />} />
              <Route path="people/:key" element={<PersonOrbitPage />} />
            </Route>
            <Route path="companies"     element={<CompaniesPage />} />
            <Route path="companies/:id" element={<OrbitPage />} />

            {/* ── Planned modules — placeholder pages ───────────────── */}
            <Route path="docs"                         element={<Navigate to={ROUTES.docs.organizations} replace />} />
            <Route path="docs/organizations"           element={<DocsOrganizationsPage />} />
            <Route path="docs/organizations/:orgId"    element={<DocsOrganizationPage />} />
            <Route path="docs/configurations"          element={<DocsConfigurationsPage />} />
            <Route path="docs/expirations"             element={<DocsExpirationsPage />} />
            {/* ── Backup module (Datto SaaS Protection) ────────────── */}
            <Route path="backup">
              <Route index element={<Navigate to={ROUTES.backup.saas} replace />} />
              <Route path="saas"     element={<BackupSaasCustomersPage />} />
              <Route path="saas/:id" element={<BackupSaasCustomerPage />} />
            </Route>

            {/* ── Pre-/psa paths (incl. /dashboards/*), kept resolving ── */}
            {LEGACY_REDIRECTS.map(({ from, to }) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            {LEGACY_RECORD_REDIRECTS.map(({ from, build }) => (
              <Route key={from} path={from} element={<LegacyRecordRedirect build={build} />} />
            ))}

            <Route path="account" element={<AccountPage />}>
              <Route path="info" element={<AccountInfoPage />} />
              <Route path="note-templates" element={<AccountNoteTemplatesPage />} />
              <Route path="bookmarks" element={<AccountBookmarksPage />} />
              <Route path="preferences" element={<AccountPreferencesPage />} />
            </Route>
            <Route path="admin" element={<AdminPage />}>
              <Route path="users"        element={<AdminUsersPage />} />
              <Route path="webhook"      element={<AdminWebhookPage />} />
              <Route path="ai-assistant" element={<AdminAiAssistantPage />} />
              <Route path="psa/project-status" element={<AdminProjectStatusPage />} />
              <Route path="cipp"         element={<AdminCippPage />} />
              <Route path="cipp/settings" element={<AdminModuleSettingsPage module="cipp" />} />
              <Route path="psa"          element={<AdminModuleSettingsPage module="psa" />} />
              <Route path="rmm"          element={<AdminModuleSettingsPage module="rmm" />} />
              <Route path="csp"          element={<AdminModuleSettingsPage module="csp" />} />
              <Route path="backup"       element={<AdminModuleSettingsPage module="backup" />} />
              <Route path="insights"     element={<AdminModuleSettingsPage module="insights" />} />
              <Route path="soc"          element={<AdminModuleSettingsPage module="soc" />} />
              {FEATURES.aiChat && <Route path="ai" element={<AdminModuleSettingsPage module="ai" />} />}
              <Route path="network"          element={<AdminNetworkMappingPage />} />
              <Route path="network/settings" element={<AdminModuleSettingsPage module="network" />} />
              <Route path="domains"          element={<AdminModuleSettingsPage module="domains" />} />
              <Route path="system"       element={<AdminSystemPage />} />
              <Route path="integrations" element={<AdminIntegrationsPage />} />
              <Route path="mappings"     element={<AdminMappingsPage />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="bottom-right" />
    </QueryClientProvider>
  </React.StrictMode>,
);
